import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@chakra-ui/react";
import { Toaster } from "sonner";
import { addDays, setHours, setMinutes, setSeconds, setMilliseconds } from "date-fns";
import { EventCalendar } from "../../../components/event-calendar";
import { ThemeToggle } from "../../../components/event-calendar/theme-toggle";
import { CalendarSidebar } from "../../../components/event-calendar/calendar-sidebar";
import { cn } from "../../../lib/utils";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import UserNoAccess from "../../../components/partials/UserNoAccess.js";
import { useTranslation } from "react-i18next";

const CALENDARS = [
  {
    id: "company",
    label: "Company",
    description: "Org-wide milestones and deadlines",
    color: "sky",
    colorClass: "bg-sky-500",
  },
  {
    id: "team",
    label: "Team",
    description: "Internal meetings and syncs",
    color: "emerald",
    colorClass: "bg-emerald-500",
  },
  {
    id: "marketing",
    label: "Marketing",
    description: "Campaign launches and events",
    color: "orange",
    colorClass: "bg-orange-500",
  },
  {
    id: "personal",
    label: "Personal",
    description: "Focus time and OOO",
    color: "rose",
    colorClass: "bg-rose-500",
  },
];

const planStatusLabels = {
  completed: "Completed",
  ongoing: "Ongoing",
  approved: "Approved",
};

const DAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const doctorLabels = {
  "dr-simona": "Dr. Simona Petrauskienė",
  "dr-tomas": "Dr. Tomas Kazlauskas",
  "dr-ieva": "Dr. Ieva Grigaitė",
};

const chairLabels = {
  "chair-1": "Operatory 1",
  "chair-2": "Operatory 2",
  "chair-3": "Operatory 3",
};

const samplePatientProfile = {
  id: "68d1590d23ddd52d0efe206a",
  patientId: "P-001",
  name: "Jonas Jonauskas",
  phone: "+37060000001",
  email: "pavyzdys@dentacasa.com",
  status: "ongoingTreatment",
  treatmentPlans: {
    "TP-001": {
      status: "completed",
      summary: "Rehabilitation phase one",
      services: [
        {
          id: "tp1-service-1",
          name: "Full mouth radiographs",
          price: 30,
          status: "completed",
        },
        {
          id: "tp1-service-2",
          name: "Professional cleaning",
          price: 100,
          status: "completed",
        },
        {
          id: "tp1-service-3",
          name: "Composite fillings (14, 34, 36)",
          price: 150,
          status: "completed",
        },
        {
          id: "tp1-service-4",
          name: "Fluoride varnish application",
          price: 60,
          status: "completed",
        },
        {
          id: "tp1-service-5",
          name: "Zirconia crowns (14, 34, 36)",
          price: 700,
          status: "completed",
        },
        {
          id: "tp1-service-6",
          name: "Custom night guard",
          price: 300,
          status: "completed",
        },
      ],
    },
    "TP-002": {
      status: "ongoing",
      summary: "Crown and implant therapy",
      services: [
        {
          id: "tp2-service-1",
          name: "Bite analysis",
          price: 30,
          status: "completed",
        },
        {
          id: "tp2-service-2",
          name: "Airflow cleaning",
          price: 50,
          status: "completed",
        },
        {
          id: "tp2-service-3",
          name: "Endodontic retreatment (22, 44, 41)",
          price: 70,
          status: "completed",
        },
        {
          id: "tp2-service-4",
          name: "Implant placement (15, 13)",
          price: 500,
          status: "scheduled",
        },
        {
          id: "tp2-service-5",
          name: "Crown preparation (14)",
          price: 300,
          status: "scheduled",
        },
      ],
    },
    "TP-003": {
      status: "approved",
      summary: "Smile design follow-up",
      services: [
        {
          id: "tp3-service-1",
          name: "Shade selection and mock-up",
          price: 50,
          status: "planned",
        },
        {
          id: "tp3-service-2",
          name: "Digital smile design review",
          price: 150,
          status: "planned",
        },
        {
          id: "tp3-service-3",
          name: "Ceramic veneer preparation",
          price: 1200,
          status: "planned",
        },
      ],
    },
  },
};

const createAppointmentEvent = ({
  id,
  calendarId,
  daysFromToday,
  hour,
  minute = 0,
  durationMinutes = 60,
  planId,
  serviceIds = [],
  appointmentLabel,
  color = "sky",
  location,
  notes,
  doctorId,
  chairId,
}) => {
  const day = addDays(new Date(), daysFromToday);
  const startWithHours = setHours(day, hour);
  const startWithMinutes = setMinutes(startWithHours, minute);
  const start = setMilliseconds(setSeconds(startWithMinutes, 0), 0);
  const end = new Date(start.getTime() + durationMinutes * 60 * 1000);

  const treatmentPlan = samplePatientProfile.treatmentPlans[planId];
  const selectedServices = serviceIds.length
    ? serviceIds
        .map((serviceId) =>
          treatmentPlan?.services.find((service) => service.id === serviceId)
        )
        .filter(Boolean)
    : treatmentPlan?.services || [];

  const serviceSummary = selectedServices
    .map((service) => service.name)
    .join(" • ");

  const descriptionLines = [
    `Patient: ${samplePatientProfile.name} (${samplePatientProfile.patientId})`,
    `Contact: ${samplePatientProfile.phone} • ${samplePatientProfile.email}`,
  ];

  if (planId) {
    const planStatus = treatmentPlan?.status
      ? planStatusLabels[treatmentPlan.status] || treatmentPlan.status
      : null;
    descriptionLines.push(
      `Treatment plan ${planId}${planStatus ? ` • ${planStatus}` : ""}`
    );
  }

  if (serviceSummary) {
    descriptionLines.push(`Services: ${serviceSummary}`);
  }

  if (doctorId) {
    const doctorName = doctorLabels[doctorId] || doctorId;
    descriptionLines.push(`Doctor: ${doctorName}`);
  }

  if (chairId) {
    const chairName =
      chairLabels[chairId] ||
      (chairId.startsWith("chair-")
        ? `Operatory ${chairId.split("-")[1]}`
        : chairId);
    descriptionLines.push(`Chair: ${chairName}`);
  }

  if (notes) {
    descriptionLines.push(`Notes: ${notes}`);
  }

  return {
    id,
    calendarId,
    title: `${samplePatientProfile.name} • ${appointmentLabel}`,
    description: descriptionLines.join("\n"),
    start,
    end,
    color,
    location,
    doctorId: doctorId || "dr-simona",
    chairId: chairId || "chair-1",
    patient: {
      id: samplePatientProfile.id,
      patientId: samplePatientProfile.patientId,
      name: samplePatientProfile.name,
      phone: samplePatientProfile.phone,
      email: samplePatientProfile.email,
      status: samplePatientProfile.status,
    },
    treatmentPlan: planId
      ? {
          id: planId,
          status: treatmentPlan?.status,
          services: selectedServices,
        }
      : undefined,
  };
};

const sampleEvents = [
  createAppointmentEvent({
    id: "tp-001-review",
    calendarId: "team",
    daysFromToday: -2,
    hour: 9,
    durationMinutes: 60,
    planId: "TP-001",
    serviceIds: ["tp1-service-5", "tp1-service-6"],
    appointmentLabel: "Post-treatment review",
    color: "emerald",
    location: "Operatory 2",
    notes: "Deliver night guard and review occlusion.",
    doctorId: "dr-ieva",
    chairId: "chair-2",
  }),
  createAppointmentEvent({
    id: "tp-002-prep",
    calendarId: "company",
    daysFromToday: 0,
    hour: 10,
    durationMinutes: 90,
    planId: "TP-002",
    serviceIds: ["tp2-service-4", "tp2-service-5"],
    appointmentLabel: "Crown prep & implant placement",
    color: "sky",
    location: "Surgery suite",
    notes: "Prep tooth 14 and place implants on 15/13 under local anesthesia.",
    doctorId: "dr-tomas",
    chairId: "chair-1",
  }),
  createAppointmentEvent({
    id: "tp-002-follow-up",
    calendarId: "marketing",
    daysFromToday: 3,
    hour: 8,
    durationMinutes: 45,
    planId: "TP-002",
    serviceIds: ["tp2-service-3"],
    appointmentLabel: "Endodontic retreatment check",
    color: "orange",
    location: "Operatory 1",
    notes: "Confirm healing and update treatment notes for canal retreatment.",
    doctorId: "dr-simona",
    chairId: "chair-1",
  }),
  createAppointmentEvent({
    id: "tp-003-design",
    calendarId: "personal",
    daysFromToday: 7,
    hour: 11,
    durationMinutes: 60,
    planId: "TP-003",
    serviceIds: ["tp3-service-1", "tp3-service-2"],
    appointmentLabel: "Smile design consultation",
    color: "rose",
    location: "Consult lounge",
    notes: "Share mock-up photos and align on veneer shape before preparation.",
    doctorId: "dr-tomas",
    chairId: "chair-3",
  }),
  createAppointmentEvent({
    id: "tp-003-prep",
    calendarId: "marketing",
    daysFromToday: 9,
    hour: 13,
    durationMinutes: 120,
    planId: "TP-003",
    serviceIds: ["tp3-service-3"],
    appointmentLabel: "Ceramic veneer preparation",
    color: "violet",
    location: "Operatory 3",
    notes: "Prep eight anterior teeth and take final impressions.",
    doctorId: "dr-tomas",
    chairId: "chair-3",
  }),
];

const SIDEBAR_STORAGE_KEY = "dentacasa-calendar-sidebar-open";
const VIEW_STORAGE_KEY = "dentacasa-calendar-view";

const CalendarMain = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [events, setEvents] = useState(sampleEvents);
  const [activeCalendars, setActiveCalendars] = useState(
    CALENDARS.map((calendar) => calendar.id)
  );
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    const storedValue = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (storedValue !== null) {
      return storedValue === "true";
    }
    return false;
  });
  const [initialView] = useState(() => {
    if (typeof window === "undefined") return "week";
    return window.localStorage.getItem(VIEW_STORAGE_KEY) || "week";
  });
  const [currentDate, setCurrentDate] = useState(new Date());

  const defaultCalendarId = CALENDARS[0]?.id || "company";

  // REDUX
  const authLogin = useSelector((state) => state.authLogin);
  const { userInfo } = authLogin;

  const tenantDetailsBySlug = useSelector((state) => state.tenantDetailsBySlug);
  const { tenant } = tenantDetailsBySlug;

  const calendarPreferences = useMemo(() => {
    const resolvedTimezone =
      tenant?.timezone ||
      (typeof Intl !== "undefined"
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : "UTC");

    const resolveHours = (value, fallback) => {
      if (!value || typeof value !== "string") return fallback;
      const [hoursString] = value.split(":");
      const parsed = Number(hoursString);
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    const defaultStartHour = resolveHours(tenant?.workingHours?.start, 8);
    const defaultEndHourRaw = resolveHours(tenant?.workingHours?.end, 17);
    const defaultEndHour = Math.max(defaultEndHourRaw, defaultStartHour + 1);

    const workingDays = tenant?.workingHours?.days || {};
    let aggregatedStart = Number.POSITIVE_INFINITY;
    let aggregatedEnd = Number.NEGATIVE_INFINITY;

    const byDay = DAY_KEYS.reduce((acc, dayKey) => {
      const dayConfig = workingDays[dayKey] || {};
      const enabled = dayConfig.enabled !== false;
      const startHour = resolveHours(dayConfig.start, defaultStartHour);
      const endHourRaw = resolveHours(dayConfig.end, defaultEndHour);
      const endHour = Math.max(endHourRaw, startHour + 1);

      if (enabled) {
        aggregatedStart = Math.min(aggregatedStart, startHour);
        aggregatedEnd = Math.max(aggregatedEnd, endHour);
      }

      acc[dayKey] = {
        enabled,
        startHour,
        endHour,
      };
      return acc;
    }, {});

    if (!Number.isFinite(aggregatedStart) || !Number.isFinite(aggregatedEnd)) {
      aggregatedStart = defaultStartHour;
      aggregatedEnd = defaultEndHour;
    }

    return {
      timezone: resolvedTimezone,
      timeFormat: tenant?.timeFormat === "12h" ? "12h" : "24h",
      weekStartsOn: tenant?.weekStart === "sunday" ? 0 : 1,
      businessHours: {
        startHour: aggregatedStart,
        endHour: aggregatedEnd,
        byDay,
      },
    };
  }, [tenant]);

  // CHECK PERMISSIONS

  const currentTenantSlug = slug || tenant?.slug;

  // 1. Get permissions for current tenant
  const currentTenantAccess = userInfo?.tenants?.find(
    (t) => t.slug === currentTenantSlug
  );

  const hasAccess = currentTenantAccess?.permissions?.appointments?.on;
  const hasAccessToDelete =
    currentTenantAccess?.permissions?.appointments?.delete;
  const hasAccessToCreate =
    currentTenantAccess?.permissions?.appointments?.create;
  const hasAccessToUpdate =
    currentTenantAccess?.permissions?.appointments?.update;

  const visibleEvents = useMemo(() => {
    if (activeCalendars.length === CALENDARS.length) {
      return events;
    }
    return events.filter((event) =>
      activeCalendars.includes(event.calendarId || defaultCalendarId)
    );
  }, [events, activeCalendars, defaultCalendarId]);

  const closeSidebar = () => setIsSidebarOpen(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      SIDEBAR_STORAGE_KEY,
      isSidebarOpen ? "true" : "false"
    );
  }, [isSidebarOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const storedView = window.localStorage.getItem(VIEW_STORAGE_KEY);
    if (!storedView) {
      window.localStorage.setItem(VIEW_STORAGE_KEY, initialView);
    }
  }, [initialView]);

  useEffect(() => {
    // Early exit if permissions are not loaded yet
    if (!currentTenantAccess?.permissions) return;

    const perms = currentTenantAccess.permissions;

    if (perms.appointments?.on) return; // ✅ allowed, stay on dashboard
  }, [currentTenantAccess, slug, navigate]);

  if (hasAccess === false)
    return (
      <>
        <UserNoAccess pageName={t("noAccess.treatmentPlans")} />
      </>
    );

  const handleEventAdd = (event) => {
    setEvents((prev) => [
      ...prev,
      {
        ...event,
        calendarId: event.calendarId || defaultCalendarId,
      },
    ]);
  };

  const handleEventUpdate = (updatedEvent) => {
    setEvents((prev) =>
      prev.map((event) => (event.id === updatedEvent.id ? updatedEvent : event))
    );
  };

  const handleEventDelete = (eventId) => {
    setEvents((prev) => prev.filter((event) => event.id !== eventId));
  };

  const handleViewChange = (nextView) => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(VIEW_STORAGE_KEY, nextView);
  };

  const toggleCalendar = (calendarId) => {
    setActiveCalendars((prev) =>
      prev.includes(calendarId)
        ? prev.filter((id) => id !== calendarId)
        : [...prev, calendarId]
    );
  };

  //LOG

  console.log(
    "hasAccess, hasAccessToCreate, hasAccessToUpdate, hasAccessToDelete",
    hasAccess,
    hasAccessToCreate,
    hasAccessToUpdate,
    hasAccessToDelete
  );
  console.log("userInfo", userInfo);
  console.log("tenant", tenant);

  return (
    <Box p="7rem 2rem">
      <Box w="100%" h="100%" borderRadius="1.5rem" overflow="hidden">
        <div id="tw-root" className="bg-background text-foreground">
          {isSidebarOpen && (
            <div
              className="fixed inset-0 z-30 bg-black/40 md:hidden"
              onClick={closeSidebar}
            />
          )}
          <div className="relative flex min-h-screen flex-col md:flex-row ">
            <aside
              className={cn(
                "z-40 w-72 border-r border-border bg-background shadow-xl transition-transform duration-200 md:static md:shadow-none",
                isSidebarOpen
                  ? "fixed inset-y-0 left-0 translate-x-0 md:translate-x-0 md:flex"
                  : "fixed inset-y-0 left-0 -translate-x-full md:-translate-x-full md:hidden"
              )}
            >
              <CalendarSidebar
                calendars={CALENDARS}
                activeCalendars={activeCalendars}
                onToggleCalendar={toggleCalendar}
                onClose={closeSidebar}
                currentDate={currentDate}
                onDateChange={(date) => setCurrentDate(date)}
              />
            </aside>
            <main className="flex flex-1 flex-col gap-6 p-4 sm:p-6 lg:p-0 ">
              {/* <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-2xl font-semibold">
                  Appointment Calendar
                </h1>
                <p className="text-muted-foreground">
                  Manage upcoming client appointments and internal events.
                </p>
              </div>
              <div className="flex items-center justify-end gap-2">
                <ThemeToggle />
              </div>
            </div> */}
              <div className="flex-1">
                <EventCalendar
                  events={visibleEvents}
                  onEventAdd={handleEventAdd}
                  onEventUpdate={handleEventUpdate}
                  onEventDelete={handleEventDelete}
                  calendars={CALENDARS}
                  onToggleSidebar={() => setIsSidebarOpen((prev) => !prev)}
                  isSidebarOpen={isSidebarOpen}
                  currentDate={currentDate}
                  onCurrentDateChange={setCurrentDate}
                  initialView={initialView}
                  onViewChange={handleViewChange}
                  timezone={calendarPreferences.timezone}
                  timeFormat={calendarPreferences.timeFormat}
                  weekStartsOn={calendarPreferences.weekStartsOn}
                  businessHours={calendarPreferences.businessHours}
                  clinicChairs={tenant?.dentalChairs || 3}
                />
              </div>
            </main>
          </div>
        </div>
        <Toaster position="bottom-left" richColors />
      </Box>
    </Box>
  );
};

export default CalendarMain;
